import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { formatINR } from '@/lib/format';
import { receiveSisSettlementPayment, type SisSettlement } from '@/api/sisSettlement';

const MODES = ['Bank Transfer', 'UPI', 'Cheque', 'Cash', 'Adjustment'];

interface Props {
  open: boolean;
  settlement: SisSettlement | null;
  onClose: () => void;
}

export function ReceivePaymentModal({ open, settlement, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState(MODES[0]);
  const [ref, setRef] = useState('');

  useEffect(() => {
    if (open && settlement) {
      setAmount(String(settlement.balance > 0 ? settlement.balance : ''));
      setMode(MODES[0]);
      setRef('');
    }
  }, [open, settlement]);

  const pay = useMutation({
    mutationFn: () => receiveSisSettlementPayment(settlement!.id, { amount: Number(amount), mode, ref: ref || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sis-settlements'] });
      toast.push('Payment recorded', 'success');
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Could not record payment'), 'error'),
  });

  if (!settlement) return null;
  const amt = Number(amount);
  const valid = Number.isFinite(amt) && amt > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Receive payment — ${settlement.settlementNo}`}
      size="sm"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!valid || pay.isPending} onClick={() => pay.mutate()}>
            {pay.isPending ? 'Saving…' : 'Record payment'}
          </Button>
        </>
      }
    >
      <div className="mb-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 text-sm">
        <div className="flex justify-between"><span className="text-[var(--text-muted)]">{settlement.storeName}</span><span>Net ₹{formatINR(settlement.netReceivable)}</span></div>
        <div className="flex justify-between"><span className="text-[var(--text-muted)]">Received</span><span>₹{formatINR(settlement.received)}</span></div>
        <div className="flex justify-between font-semibold"><span>Balance</span><span className="text-accent">₹{formatINR(settlement.balance)}</span></div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormGroup label="Amount received" required>
          <Input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="text-right" />
        </FormGroup>
        <FormGroup label="Mode">
          <Select value={mode} onChange={(e) => setMode(e.target.value)}>
            {MODES.map((m) => <option key={m}>{m}</option>)}
          </Select>
        </FormGroup>
        <FormGroup label="Reference" className="sm:col-span-2">
          <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="UTR / cheque no. / note" />
        </FormGroup>
      </div>
      {valid && amt > settlement.balance && (
        <p className="mt-2 text-xs text-warning">Amount exceeds the outstanding balance — the settlement will show as overpaid.</p>
      )}
    </Modal>
  );
}
